import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isBoss, seesAllOrders } from '@wf/shared'
import { useAuth } from '../app/auth'
import { AuswertungPanel } from './AuswertungPanel'
import { AbsencesPanel } from './AbsencesPanel'
import { CorrectionsInbox } from './CorrectionsInbox'
import { HeutePanel } from './HeutePanel'
import { MyTimePanel } from './MyTimePanel'
import { NachweisPanel } from './NachweisPanel'

type Tab = 'mine' | 'heute' | 'auswertung' | 'nachweis' | 'antraege' | 'abwesenheiten'

/**
 * Reiter „Zeiten": „Meine Zeit" für JEDEN, „Heute" (Team-Timeline) und „Auswertung" (Soll/Ist/Saldo)
 * für Chef/Zweitchef, dazu der rechtlich maßgebliche „Nachweis" (Monatsnachweis je MA, Entwerten/
 * Nachtragen/CSV) und der Antrags-Posteingang. „Korrigieren" in Heute/Auswertung springt mit
 * vorgewähltem Mitarbeiter in den Nachweis. Tab-Sichtbarkeit spiegelt die Server-Guards — reine UX, der Server
 * erzwingt die Rechte ohnehin. Bewusst OHNE Emojis in Titel/Reitern (Simon, 08.07.2026: clean wie
 * die übrigen Reiter — kein Zeitsymbol, klare Beschriftungen).
 */
export function ZeitenPage() {
  const { user } = useAuth()
  const [params] = useSearchParams()
  // Deep-Link aus einer Benachrichtigung (z. B. „/zeiten?tab=antraege"): den Reiter vorwählen, sofern gültig.
  // Ist er für die Rolle nicht sichtbar, fällt die active-Logik unten ohnehin auf „mine" zurück.
  const [tab, setTab] = useState<Tab>(() => {
    const t = params.get('tab')
    return t === 'heute' || t === 'auswertung' || t === 'nachweis' || t === 'antraege' || t === 'abwesenheiten' ? t : 'mine'
  })
  const [nachweisUser, setNachweisUser] = useState<number | null>(null)
  // Der useState-Initialwert greift nur beim ERSTEN Rendern. Wer schon auf /zeiten steht und dann eine
  // Benachrichtigung mit „?tab=…" antippt, bliebe sonst auf seinem alten Reiter — der Deep-Link liefe
  // wirkungslos ins Leere (genau der Weg, den jede Chef-Meldung nimmt).
  const tabParam = params.get('tab')
  useEffect(() => {
    if (tabParam === 'heute' || tabParam === 'auswertung' || tabParam === 'nachweis' || tabParam === 'antraege' || tabParam === 'abwesenheiten') {
      setTab(tabParam)
    }
  }, [tabParam])
  if (!user) return null
  if (user.isSuperadmin) {
    return (
      <section className="wf-view">
        <h2 className="viewtitle">Zeiten</h2>
        <p className="sub">Der Betreiber-Account hat keine eigene Zeiterfassung — wähle eine Werkstatt in der App-Verwaltung.</p>
      </section>
    )
  }

  const boss = isBoss(user)
  const showTeam = seesAllOrders(user)
  const tabs: { key: Tab; label: string; visible: boolean }[] = [
    { key: 'mine', label: 'Meine Zeit', visible: true },
    { key: 'heute', label: 'Heute', visible: showTeam },
    { key: 'auswertung', label: 'Auswertung', visible: boss },
    { key: 'nachweis', label: 'Nachweis', visible: boss },
    { key: 'antraege', label: 'Anträge', visible: boss },
    { key: 'abwesenheiten', label: 'Abwesenheiten', visible: boss },
  ]
  const active = tabs.find((t) => t.key === tab && t.visible)?.key ?? 'mine'
  const jumpToNachweis = (userId: number) => {
    setNachweisUser(userId)
    setTab('nachweis')
  }

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Zeiten</h2>
          <p className="sub">Stempeln · Monatsnachweise · Soll/Ist · Korrekturen</p>
        </div>
      </div>

      {/* Repo-Konvention für Chip-Umschalter (role=group + aria-pressed); --tabs gibt den Reitern Luft. */}
      {tabs.filter((t) => t.visible).length > 1 ? (
        <div className="wf-chips wf-chips--tabs" role="group" aria-label="Zeiten-Bereiche">
          {tabs
            .filter((t) => t.visible)
            .map((t) => (
              <button
                key={t.key}
                type="button"
                aria-pressed={active === t.key}
                className={active === t.key ? 'wf-chip wf-chip--active' : 'wf-chip'}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
        </div>
      ) : null}

      {active === 'mine' ? <MyTimePanel user={user} /> : null}
      {active === 'heute' && showTeam ? <HeutePanel user={user} onCorrect={jumpToNachweis} /> : null}
      {active === 'auswertung' && boss ? <AuswertungPanel onCorrect={jumpToNachweis} /> : null}
      {active === 'nachweis' && boss ? (
        <NachweisPanel key={nachweisUser ?? 'me'} user={user} initialUserId={nachweisUser ?? undefined} />
      ) : null}
      {active === 'antraege' && boss ? <CorrectionsInbox /> : null}
      {active === 'abwesenheiten' && boss ? <AbsencesPanel /> : null}
    </section>
  )
}
